import type { ReactElement } from 'react';
import { useEffect, useRef } from 'react';

import { HeapCustomEvent } from 'config';
import HeapAnalytics from 'services/HeapAnalytics';
import useIsInViewport from 'hooks/useIsInViewPort';

import { useCart } from 'contexts/Cart';
import CartProduct from 'models/CartProduct';

import FavoriteActions from 'components/shared/Favorite';

import RecipeImage from './RecipeImage';
import RecipeContent from './RecipeContent';
import RecipeActions from './RecipeActions';

type Props = {
  cartProduct: CartProduct;
  hasFavoritesEnabled: boolean;
  isUpsell?: boolean;
};

function Recipe({
  cartProduct,
  hasFavoritesEnabled,
  isUpsell = false,
}: Props): ReactElement {
  const { cart } = useCart();
  const { currentItem, isSoldOut, isPremiumItem } = cartProduct;
  const {
    quantity,
    sku,
    sale_tag_enabled: salesTagEnabled,
  } = currentItem;

  const { title, image_versions: imageVersions } = sku;
  const { id: cartId, hasChanged, hasError } = cart;
  const isDisabled = !cart.isUpdatable(cartProduct);

  const ref = useRef(null);
  // @ts-expect-error TODO: fix null safety
  const isInViewport = useIsInViewport(ref);

  useEffect(() => {
    if (isUpsell && isInViewport) {
      HeapAnalytics.track(HeapCustomEvent.EDIT_CART_UPSELL_VIEWED, {
        sku: cartProduct.currentItem.sku.id,
      });
    }
  }, [isInViewport, cartProduct.currentItem.sku.id, isUpsell]);

  return (
    <div
      ref={ref}
      className={[
        `col-${isUpsell ? 10 : 12}`,
        `col-sm-${isUpsell ? 5 : 6}`,
        'col-lg-4',
        'c-recipe',
        quantity > 0 ? 'is-selected' : '',
        isSoldOut ? 'is-sold-out' : '',
        isDisabled ? 'is-disabled' : '',
      ].join(' ')}
    >
      <div className="c-recipe__container">
        <RecipeImage
          imageVersions={imageVersions}
          title={title}
          isPremiumTagEnabled={isPremiumItem}
          salesTagEnabled={salesTagEnabled}
        />
        <RecipeContent
          cartId={cartId}
          cartProduct={cartProduct}
          hasCartChanged={hasChanged}
          hasCartError={hasError}
        />
        <RecipeActions
          cartProduct={cartProduct}
          isUpsell={isUpsell}
          menuId={cart.menu_id}
        />
      </div>
      {hasFavoritesEnabled && <FavoriteActions item={currentItem} />}
    </div>
  );
}

export default Recipe;
